import type { ElementType, MouseEvent } from 'react';

import { cx } from '@hrworks/design-system/functions';

import { S } from './Button.styles';
import type { ButtonProps } from './Button.types';

export const Button = <C extends ElementType = 'button'>({
  children,
  component,
  corner = 'rounded',
  disabled,
  fullWidth,
  leftIcon,
  rightIcon,
  size = 'medium',
  textAlign,
  type = 'button',
  variant = 'filled',
  color = variant === 'text' ? 'secondary' : 'primary',
  className,
  ref,
  href,
  onClick: onClickFromProps,
  ...otherProps
}: ButtonProps<C>) => {
  const Element = component || (href ? 'a' : 'button');

  const isButton = Element === 'button';
  const onClick = (e: MouseEvent<HTMLElement>) => {
    if (disabled) {
      e.preventDefault();

      return;
    }
    onClickFromProps?.(e);
  };

  return (
    <S.Button
      className={cx(S.cls.Button, className)}
      as={Element}
      {...(isButton
        ? { disabled, type }
        : {
            href,
            ...(disabled && { 'aria-disabled': true, tabIndex: -1 }),
            ...(Element !== 'a' && { role: 'button' as const }),
          })}
      ref={ref}
      $color={color}
      $corner={corner}
      $fullWidth={fullWidth}
      $size={size}
      $textAlign={textAlign}
      $variant={variant}
      onClick={onClick}
      {...otherProps}
    >
      {leftIcon && <S.Icon className={S.cls.Icon}>{leftIcon}</S.Icon>}
      {children}
      {rightIcon && <S.Icon className={S.cls.Icon}>{rightIcon}</S.Icon>}
    </S.Button>
  );
};
